import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { reduce, sum, union } from 'lodash';
import { add, format } from 'date-fns';
import PropTypes from 'prop-types';
import { CalendarNav, CalendarNext, CalendarPrev } from '@mobiscroll/react';
import { reservationCreations, trackError } from 'utils/helpers';
import { FORMAT_QT_DATE } from 'utils/helpers/datetime';
import Calendar from 'components/calendar';
import Bedding from 'components/experience/booking_form/bedding';
import PickUpDropOff from 'components/experience/booking_form/pick_up_drop_off';
import { renderPassengerTypes } from 'components/passenger_types';
import {
  getAllResources,
  getBookability,
  getProductResources,
} from 'redux/selectors/booking_form';
import { renderExtraPicks } from './extra_picks';
import { formSchema, getLabels } from './helpers';
import Price from './price';
import ResourceOptions from './resource_options';
import SubmitButton from './submit_button';
import { useCalendarAvailability } from './use_calendar_availability';
import { useCalendarPrice } from './use_calendar_price';
import { useResourceBookability } from './use_resource_bookability';
import { useResourcePrice } from './use_resource_price';

const formatDate = (date) => format(date, FORMAT_QT_DATE);
const customWithNavButtons = () => (
  <>
    <CalendarPrev />
    <CalendarNav />
    <CalendarNext />
  </>
);

export function BookingForm({
  resourceIDs,
  passengerTypeIDs = [],
  initialDate,
  initialPassengers,
  giftVoucher,
  resourceTitle,
  hideSeats = false,
}) {
  const today = initialDate ? new Date(initialDate) : new Date();
  const { independentPassengerTypes, soloChildWarningMessage } =
    window.ecom.craftConfig?.bookingPreferences?.passengerForm || {};
  if (giftVoucher) {
    // Set initialDate to tomorrow if it's a gift
    initialDate = formatDate(add(new Date(), { days: 1 }));
  }
  const defaultPassengers =
    initialPassengers ||
    reduce(
      passengerTypeIDs,
      (result, id) => {
        result[id] = id === 1 ? 1 : 0;
        return result;
      },
      {}
    );

  // Resources should be from the redux store?
  const dispatch = useDispatch();

  const allResources = useSelector(getAllResources);
  const resources = useSelector((state) =>
    getProductResources(state, { resourceIDs })
  );
  const [submissionError, setSubmissionError] = useState(false);

  const onSubmit = (values, { setSubmitting, isSubmitting }) => {
    if (isSubmitting) {
      return;
    }

    const params = {
      resource_id: values.resource,
      first_travel_date: values.date,
      passenger_types: { ...values.passengers },
      extra_pick_ids: values.extraPicks,
      discard_existing: false,
      bed_requirement_id: values.bedRequirement,
    };

    if (values.showPickUpDropOff) {
      params.suboptions = {
        [values.pickUpResourceGroup]: {
          from_route_stop_id: values.pickUp,
        },
        [values.dropOffResourceGroup]: {
          to_route_stop_id: values.dropOff,
        },
      };
    }

    const resourceType = resources[values.resource].type;
    const createAction = reservationCreations[resourceType];

    setSubmitting(true);
    setSubmissionError(false);
    dispatch(createAction(params))
      .then(() => {
        window.location = '/cart';
      })
      .catch((error) => {
        trackError(error);
        setSubmissionError(true);
        setSubmitting(false);
      });
  };

  const drawer = useMemo(
    () => document.querySelector('[data-booking-drawer]'),
    []
  );

  // If no drawer, set canvasOpened to true so the booking form is always visible
  const [canvasOpened, setCanvasOpened] = useState(drawer === null);

  useEffect(() => {
    drawer?.addEventListener('show.bs.offcanvas', () => setCanvasOpened(true), {
      once: true,
    });
  }, [drawer]);

  if (!canvasOpened) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        resource: resourceIDs && resourceIDs[0],
        date: initialDate,
        passengers: defaultPassengers,
        extraPicks: [],
      }}
      validationSchema={formSchema({
        independentPassengerTypes,
        soloChildWarningMessage,
      })}
      onSubmit={onSubmit}
      validateOnMount
      validateOnBlur={false}
    >
      {({ values, setFieldValue }) => {
        const { resource, date, passengers, extraPicks } = values;
        const passengersCount = sum(Object.values(passengers));
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [currentMonth, setCurrentMonth] = useState(
          initialDate ? new Date(initialDate) : new Date()
        );
        const selectedResource = resources[resource];
        const pricingList = selectedResource?.pricing?.calendar;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const calendarIns = useRef();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const priceLabels = useMemo(
          () => getLabels(currentMonth, pricingList),
          [pricingList, currentMonth]
        );
        const showPickUpDropOff = Boolean(
          selectedResource?.package_options?.length
        );
        const showBedding = Boolean(selectedResource?.bed_requirements?.length);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { isFetching: isFetchingAvailability } = useCalendarAvailability({
          resource,
          currentMonth,
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { isFetching: isFetchingBookability } = useCalendarPrice({
          resource,
          currentMonth,
          passengerTypeIDs,
        });
        const isFetching = isFetchingAvailability || isFetchingBookability;

        // Some reset after we change the basic resourse
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          setFieldValue('date', initialDate);
          calendarIns.current?.setVal(initialDate);
          calendarIns.current?.navigate(today);
          setCurrentMonth(today);
          setFieldValue('passengers', defaultPassengers);

          setFieldValue('showPickUpDropOff', showPickUpDropOff);
          setFieldValue('showBedding', showBedding);
          setFieldValue('bedRequirement', undefined);
          setFieldValue('extraPicks', []);

          if (showPickUpDropOff) {
            // Set the first option as default
            const [pickUps, dropOffs] = selectedResource.package_options;

            setFieldValue('pickUp', pickUps.options[0].id);
            setFieldValue('pickUpResourceGroup', pickUps.resource_group.id);
            setFieldValue('dropOff', dropOffs.options[0].id);
            setFieldValue('dropOffResourceGroup', dropOffs.resource_group.id);
          } else {
            // Remove values
            [
              'pickUp',
              'pickUpResourceGroup',
              'dropOff',
              'dropOffResourceGroup',
            ].forEach((field) => {
              setFieldValue(field, undefined);
            });
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [resource]);

        const { childResources = [] } = selectedResource || {};
        const childResourceIds = childResources.map((child) => child.id);

        // Fetch pricing for all the passenger types
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useResourcePrice({
          resourceId: resource,
          childResourceIds,
          date,
          passengerTypeIDs,
        });

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useResourceBookability({
          resourceId: resource,
          childResourceIds,
          date,
          passengers,
          passengersCount,
        });

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const bookability = useSelector((state) =>
          getBookability(state, {
            selectedResourceId: resource,
            selectedDate: date,
          })
        );

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const unavailableDates = useSelector((state) => {
          if (resource === undefined) return [];
          if (state.availability[resource] === undefined) return [];

          const {
            full_allotment,
            inactive_service,
            no_service,
            online_restriction,
          } = state.availability[resource];

          return union(
            full_allotment,
            inactive_service,
            no_service,
            online_restriction
          );
        });

        return (
          <Form>
            <div className="p-4 space-y-3">
              <div>
                <label htmlFor="resource">{resourceTitle || 'Time'}</label>
                <Field
                  name="resource"
                  id="resource"
                  className="form-select"
                  component="select"
                >
                  <ResourceOptions
                    resources={resources}
                    resourceIDs={resourceIDs}
                  />
                </Field>
                <ErrorMessage
                  name="resource"
                  component="div"
                  className="text-danger text-x-small"
                  id="error-resource"
                />
              </div>
              {!giftVoucher && (
                <div>
                  <label htmlFor="date">
                    Date <sup className="text-xx-small text-danger">*</sup>
                  </label>
                  <div className="input-with-icon input-with-icon--on-right">
                    <Field
                      component={Calendar}
                      inputProps={{
                        placeholder: 'Date',
                        readOnly: true,
                        name: 'date',
                      }}
                      className="form-control w-100"
                      labels={priceLabels}
                      invalid={unavailableDates}
                      onChange={({ value }) => {
                        if (value && !isFetching) {
                          setFieldValue('date', formatDate(value));
                        }
                      }}
                      onPageChange={({ month }) => {
                        setTimeout(() => {
                          setCurrentMonth(month);
                        });
                      }}
                      onInit={(event, ins) => {
                        calendarIns.current = ins;
                      }}
                      onClose={() => {
                        setCurrentMonth(calendarIns.current?.getVal() || today);
                      }}
                      cssClass="mobiscroll-classic"
                      renderCalendarHeader={customWithNavButtons}
                      loading={isFetching}
                    />
                    <div className="input-with-icon__icon">
                      <i className="far fa-calendar-alt" />
                    </div>
                  </div>
                  <ErrorMessage
                    name="date"
                    component="div"
                    className="text-danger text-x-small mt-3"
                    id="error-date"
                  />
                </div>
              )}
            </div>
            {renderPassengerTypes(passengerTypeIDs, resource, date)}
            {showPickUpDropOff && (
              <div className="p-4 space-y-3">
                <PickUpDropOff locations={selectedResource.package_options} />
              </div>
            )}
            {showBedding && (
              <div className="p-4 space-y-3">
                <Bedding options={selectedResource.bed_requirements} />
              </div>
            )}
            {renderExtraPicks(resource, date, passengers)}
            <div className="space-y-4 p-4 border-top border-divider">
              <Price
                date={date}
                resource={resource}
                extraPicks={extraPicks}
                allResources={allResources}
              />
              {submissionError && (
                <div className="text-danger">Error creating reservation</div>
              )}
              <SubmitButton
                bookability={bookability}
                resource={selectedResource}
                passengersCount={passengersCount}
                hideSeats={hideSeats}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

BookingForm.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  resourceIDs: PropTypes.array,
  passengerTypeIDs: PropTypes.array,
  initialDate: PropTypes.string,
  initialPassengers: PropTypes.object,
  giftVoucher: PropTypes.number,
  resourceTitle: PropTypes.string,
  hideSeats: PropTypes.bool,
};
